/**
 * CreateVolumeModal Component
 *
 * Modal for creating a new volume resource.
 * IMPORTANT: Accepts MB input and file extensions (with dots).
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useCreateVolume } from '../hooks/useCreateVolume';
import { volumeCreateSchema } from '../../../lib/validators/volume';
import { z } from 'zod';

/**
 * Generate volume code from name
 * Converts "My Documents" to "my-documents"
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
};

// Frontend schema with MB input
const createVolumeFormSchema = z.object({
  volumeCode: z
    .string()
    .min(3, 'Code must be at least 3 characters')
    .max(50, 'Code must not exceed 50 characters')
    .regex(/^[a-z0-9-]+$/, 'Code must contain only lowercase letters, numbers, and hyphens'),
  volumeName: z
    .string()
    .min(1, 'Volume name must be at least 1 character long')
    .max(100, 'Volume name must not exceed 100 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  maxFileSizeMB: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true;
        const num = parseFloat(val);
        return !isNaN(num) && num > 0 && num <= 5120;
      },
      'Max file size must be between 0 and 5120 MB (5 GB)'
    ),
  allowedFileExtensions: z.string().optional(), // Comma-separated string
});

type CreateVolumeFormData = z.infer<typeof createVolumeFormSchema>;

interface CreateVolumeModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * CreateVolumeModal component
 */
export const CreateVolumeModal: React.FC<CreateVolumeModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [autoGenerateCode, setAutoGenerateCode] = useState<boolean>(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CreateVolumeFormData>({
    resolver: zodResolver(createVolumeFormSchema),
    defaultValues: {
      volumeCode: '',
      volumeName: '',
      description: '',
      maxFileSizeMB: '',
      allowedFileExtensions: '',
    },
  });

  const watchName = watch('volumeName');

  // Auto-generate volume code from name
  useEffect(() => {
    if (autoGenerateCode && watchName) {
      const generatedCode = generateCodeFromName(watchName);
      setValue('volumeCode', generatedCode);
    }
  }, [watchName, autoGenerateCode, setValue]);

  const { mutate: createVolume, isPending } = useCreateVolume(tenantId, {
    onSuccess: () => {
      setSuccessMessage('Volume created successfully!');
      setErrorMessage('');
      setTimeout(() => {
        reset();
        onClose();
        setSuccessMessage('');
      }, 1500);
    },
    onError: (error) => {
      setErrorMessage(error);
      setSuccessMessage('');
    },
  });

  const onSubmit = (data: CreateVolumeFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Parse max file size in MB (allow empty for unlimited)
    const maxFileSize = data.maxFileSizeMB && data.maxFileSizeMB !== ''
      ? parseFloat(data.maxFileSizeMB)
      : undefined;

    // Parse allowed file extensions (comma-separated)
    // IMPORTANT: Send empty array if field is empty (means all extensions allowed)
    const allowedFileExtensions = data.allowedFileExtensions && data.allowedFileExtensions.trim() !== ''
      ? data.allowedFileExtensions
          .split(',')
          .map((ext) => ext.trim())
          .filter((ext) => ext !== '')
          .map((ext) => ext.startsWith('.') ? ext : `.${ext}`) // Ensure extensions start with dot
      : [];

    // Create volume DTO
    const volumeData = {
      volumeCode: data.volumeCode,
      volumeName: data.volumeName,
      description: data.description || undefined,
      maxFileSize,
      // Always send allowedFileExtensions (empty array = all extensions allowed)
      allowedFileExtensions,
    };

    createVolume(volumeData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setAutoGenerateCode(true);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Volume</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Volume Name */}
          <FormField label="Volume Name" required error={errors.volumeName?.message}>
            <Input
              {...register('volumeName')}
              placeholder="my-volume"
              error={!!errors.volumeName}
            />
          </FormField>

          {/* Volume Code */}
          <FormField label="Volume Code" required error={errors.volumeCode?.message}>
            <Input
              {...register('volumeCode')}
              placeholder="e.g., my-documents"
              error={!!errors.volumeCode}
              disabled={autoGenerateCode}
            />
            <div className="mt-2 flex items-center">
              <Checkbox
                id="autoGenerateVolumeCode"
                checked={autoGenerateCode}
                onChange={(e) => setAutoGenerateCode(e.target.checked)}
              />
              <label htmlFor="autoGenerateVolumeCode" className="ml-2 text-sm text-gray-600">
                Auto-generate code from name
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable after creation. Use lowercase letters, numbers, and hyphens.
            </p>
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this volume..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Max File Size in MB */}
          <FormField
            label="Max File Size (MB)"
            error={errors.maxFileSizeMB?.message}
            helperText="Maximum size for individual files (leave empty for unlimited)"
          >
            <Input
              {...register('maxFileSizeMB')}
              type="number"
              step="0.01"
              min="0"
              max="5120"
              placeholder="100"
              error={!!errors.maxFileSizeMB}
            />
          </FormField>

          {/* Allowed File Extensions */}
          <FormField
            label="Allowed File Extensions"
            error={errors.allowedFileExtensions?.message}
            helperText="Comma-separated extensions (e.g., .pdf, .jpg, .png). Leave empty for all extensions."
          >
            <Input
              {...register('allowedFileExtensions')}
              placeholder=".pdf, .jpg, .png, .docx"
              error={!!errors.allowedFileExtensions}
            />
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Create Volume
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
